// @flow
import React, {useState} from 'react';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import {Divider, Grid, Typography, makeStyles} from '@material-ui/core';

import {colors} from '../../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../../utils/component';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {commaFormat} from '../../../../utils/number';
import {useStoreCurrency} from '../../../currency/utils';

import type {Tax} from '../../../../entities';

type Props = {
  taxes: Tax[],
  price: string,
  taxedPrice: string,
};

const useStyles = makeStyles(theme => ({
  title: {
    color: '#1F2037',
    fontSize: 18,
    fontWeight: 600,
  },
  icon: {
    color: '#6B6C80',
  },
  btn: {
    background: 'transparent',
    color: '#6B6C80',
  },
  divider: {
    margin: theme.spacing(1, 0),
    backgroundColor: colors.grey300,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 350,

    padding: theme.spacing(4, 1, 2, 1),
  },
  customModal: {
    '& .MuiPaper-root': {
      padding: '16px!important',
      maxWidth: '80%',
    },
  },
  summary: {
    width: '100%',
    backgroundColor: '#F3F3F6',
    padding: theme.spacing(1.5),
    borderRadius: 12,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  description: {
    color: '#4B4C63',
    fontWeight: 500,
    fontSize: 14,
  },
  realPrice: {
    color: colors.grey900,
    fontWeight: 500,
  },
}));

const ResumeTaxModal = ({taxes, price, taxedPrice}: Props) => {
  const currency = useStoreCurrency();

  const hasTaxesFeature = useIsFeatureInTier('TAXES');
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const classes = useStyles();

  if (!hasTaxesFeature || taxes.length === 0) return null;

  if (!(price && taxedPrice)) return null;

  const calcTaxAmount = (price: string, percentage: string) => {
    if (!price) return '--';

    return parseFloat(Number(price) * Number(percentage)).toFixed(2);
  };

  return (
    <div>
      <CustomButton
        className={classes.btn}
        endIcon={<AddCircleOutlineIcon className={classes.icon} />}
        onClick={() => setIsOpenModal(true)}
      >
        Resumen de impuestos
      </CustomButton>

      <CustomModal
        open={isOpenModal}
        handleCloseModal={() => setIsOpenModal(false)}
        className={classes.customModal}
      >
        <div className={classes.root}>
          <Typography className={classes.title}>
            Resumen de impuestos
          </Typography>

          <Divider className={classes.divider} />

          <div className={classes.summary}>
            <Grid spacing={1}>
              <Grid item xs={12}>
                <div className={classes.row}>
                  <Typography className={classes.description}>
                    Valor de venta:
                  </Typography>

                  <Typography className={classes.description}>
                    {commaFormat(price ?? '--', currency?.symbol)}
                  </Typography>
                </div>
              </Grid>

              {taxes.map(t => (
                <Grid key={t.id} item xs={12}>
                  <div className={classes.row}>
                    <Typography className={classes.description}>
                      {t.label} - {Number(t.percentage) * 100}%
                    </Typography>

                    <Typography className={classes.description}>
                      +{' '}
                      {commaFormat(
                        calcTaxAmount(price, t.percentage),
                        currency?.symbol
                      )}
                    </Typography>
                  </div>
                </Grid>
              ))}
              <Divider className={classes.divider} />

              <Grid item xs={12}>
                <div className={classes.row}>
                  <Typography className={classes.realPrice}>
                    Precio de venta:
                  </Typography>

                  <Typography className={classes.realPrice}>
                    {commaFormat(taxedPrice ?? '--', currency?.symbol)}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default ResumeTaxModal;
